import {useEffect, useState} from 'react';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Visibility from '@mui/icons-material/Visibility';
import {Button, CircularProgress, IconButton, InputAdornment} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import {useForm} from 'react-hook-form';
import {FormContainer, TextFieldElement} from 'react-hook-form-mui';
import {useNavigate} from 'react-router-dom';
import { useMutation } from "@tanstack/react-query";
import { API_URLS, axiosRequest } from "@lib/axios";

/**
 * Sets new password as part of the Forgot Password flow.
 * For the standard "Change Password" page, see components/Pages/ChangePassword/ChangePassword.js
 */
const ChangePasswordForm = ({user, code, setPageMessage, setPageErrorMessage }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const navigate = useNavigate();

  const {
    handleSubmit,
    control,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    mode: 'onSubmit',
  });

  const [password, confirmPassword] = watch(['password', 'confirmPassword']);

  useEffect(() => {
    if (errors?.confirmPassword?.type === 'match') {
      if (password === confirmPassword) {
        clearErrors();
      }
    }
  }, [errors, password, confirmPassword, clearErrors]);

  const setPasswordMutation = useMutation({
    mutationFn: async (data) => axiosRequest({
      method: 'POST',
      url: `${API_URLS.AUTH}/confirm-forgot-password`,
      data,
    }),
    onSuccess: async () => {
      navigate('/login', { state: { message: 'Your password has been updated.' }});
      setPageErrorMessage('');
    },
    onError: async (err) => {
      console.error('[ChangePasswordForm] Error updating password:', err);
      setPageErrorMessage(err);
    },
    onSettled: () => {
      setSubmitting(false);
    },
  });

  const handlePassword = () => {
    setSubmitting(true);
    setPageMessage('');
    setPageErrorMessage('');

    if (password !== confirmPassword) {
      setError('confirmPassword', { type: 'match', message: 'Passwords must match' });
      setSubmitting(false);
      return false;
    }

    const input = {
      user,
      code,
      password
    };
    setPasswordMutation.mutate(input);
  };

  return (
    <FormContainer onSuccess={handleSubmit(handlePassword)} FormProps={{ className: 'mui' }}>
      <Grid container spacing={3}>
        {submitting && (
          <Grid xs={12} display="flex" justifyContent="center">
            <CircularProgress/>
          </Grid>
        )}
        {!submitting && (
          <>
            <Grid xs={12}>
              <TextFieldElement
                id="field-password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                label={'Password'}
                control={control}
                placeholder="Enter your password here"
                required
                fullWidth
                validation={{
                  minLength: {
                    value: 10,
                    message: 'Password must have at least 10 characters'
                  }
                }}
                InputProps={{
                  endAdornment:
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        // onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff/> : <Visibility/>}
                      </IconButton>
                    </InputAdornment>
                }}
              />
            </Grid>
            <Grid xs={12}>
              <TextFieldElement
                id="field-confirm_password"
                name="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                label={'Confirm Password'}
                control={control}
                placeholder="Confirm your password here"
                required
                fullWidth
                validation={{
                  minLength: {
                    value: 10,
                    message: 'Password must have at least 10 characters'
                  }
                }}
                InputProps={{
                  endAdornment:
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        // onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff/> : <Visibility/>}
                      </IconButton>
                    </InputAdornment>
                }}
              />
            </Grid>
            <Grid xs={12}>
              <Button
                id="button-submit"
                variant="contained"
                size="small"
                type="submit"
                fullWidth
                sx={{ marginTop: '20px' }}
              >
                Submit
              </Button>
            </Grid>
          </>
        )}
      </Grid>
    </FormContainer>
  );
};

export default ChangePasswordForm;
